import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Database, Layers, Check, Share2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CreateProposalPage() {
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState('');
  const [schemaTree, setSchemaTree] = useState(null);
  const [selectedAttrs, setSelectedAttrs] = useState(new Set());
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get('/schemas').then(r => setSchemas(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedSchema) { setSchemaTree(null); return; }
    setLoading(true);
    api.get(`/schemas/${selectedSchema}/full`).then(r => {
      setSchemaTree(r.data);
      // Auto-select all attributes
      const allIds = new Set();
      (r.data.entities || []).forEach(e => {
        (e.groups || []).forEach(g => g.attributes.forEach(a => allIds.add(a.attribute_id)));
        (e.ungrouped_attributes || []).forEach(a => allIds.add(a.attribute_id));
      });
      setSelectedAttrs(allIds);
    }).catch(console.error).finally(() => setLoading(false));
  }, [selectedSchema]);

  const toggleAttr = (id) => {
    setSelectedAttrs(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleEntity = (entity) => {
    const ids = [];
    (entity.groups || []).forEach(g => g.attributes.forEach(a => ids.push(a.attribute_id)));
    (entity.ungrouped_attributes || []).forEach(a => ids.push(a.attribute_id));
    const allSelected = ids.every(id => selectedAttrs.has(id));
    setSelectedAttrs(prev => {
      const next = new Set(prev);
      ids.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const handleCreate = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    if (!selectedSchema) { toast.error('Select a schema'); return; }
    if (selectedAttrs.size === 0) { toast.error('Select at least one attribute'); return; }
    setCreating(true);
    try {
      const r = await api.post('/proposals', {
        title: title.trim(), description: description.trim(),
        schema_id: selectedSchema, attribute_ids: [...selectedAttrs],
      });
      toast.success('Proposal created');
      navigate(`/proposals/${r.data.proposal_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="create-proposal-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/proposals')} className="h-7 px-2 rounded-none text-[#4B5563]" data-testid="back-to-proposals">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Proposals
          </Button>
          <span className="text-[#9CA3AF]">/</span>
          <span className="text-sm font-medium text-[#0A0A0A]">New Proposal</span>
        </div>

        <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A] mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>Create Proposal</h1>

        <div className="space-y-6">
          {/* Title & Description */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Proposal Title</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} className="rounded-none mt-1" data-testid="proposal-title-input" placeholder="e.g., Breast Cancer v2 Schema Proposal" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Schema</Label>
              <Select value={selectedSchema} onValueChange={setSelectedSchema}>
                <SelectTrigger className="rounded-none mt-1" data-testid="schema-select">
                  <SelectValue placeholder="Select a schema..." />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {schemas.map(s => (
                    <SelectItem key={s.schema_id} value={s.schema_id} className="text-xs">{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#4B5563]">Description (optional)</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} className="rounded-none mt-1" data-testid="proposal-desc-input" rows={2} placeholder="Brief description for the recipient..." />
          </div>

          {/* Attribute Selection */}
          {loading && <div className="flex items-center justify-center py-8"><div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div>}

          {schemaTree && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs font-semibold text-[#4B5563]">Select Attributes to Include ({selectedAttrs.size} selected)</Label>
              </div>
              <div className="border border-[#E5E7EB]" data-testid="attr-selector">
                {(schemaTree.entities || []).map(entity => {
                  const entityAttrIds = [];
                  (entity.groups || []).forEach(g => g.attributes.forEach(a => entityAttrIds.push(a.attribute_id)));
                  (entity.ungrouped_attributes || []).forEach(a => entityAttrIds.push(a.attribute_id));
                  const allEntitySelected = entityAttrIds.every(id => selectedAttrs.has(id));

                  return (
                    <div key={entity.entity_id}>
                      <div className="flex items-center gap-3 px-4 py-2.5 bg-[#F9FAFB] border-b border-[#E5E7EB]" data-testid={`entity-sel-${entity.entity_id}`}>
                        <Checkbox checked={allEntitySelected} onCheckedChange={() => toggleEntity(entity)} className="h-4 w-4 rounded-none" />
                        <Layers className="w-4 h-4 text-[#002FA7]" strokeWidth={1.5} />
                        <span className="text-sm font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>{entity.name}</span>
                        <span className="text-[10px] text-[#9CA3AF]">{entityAttrIds.filter(id => selectedAttrs.has(id)).length}/{entityAttrIds.length}</span>
                      </div>
                      {(entity.groups || []).map(g => (
                        <div key={g.group_id}>
                          {g.attributes.map(a => (
                            <div key={a.attribute_id} className="flex items-center gap-3 px-4 py-1.5 pl-12 border-b border-[#E5E7EB] last:border-b-0">
                              <Checkbox checked={selectedAttrs.has(a.attribute_id)} onCheckedChange={() => toggleAttr(a.attribute_id)} className="h-3.5 w-3.5 rounded-none" data-testid={`check-${a.attribute_id}`} />
                              <span className="text-sm text-[#4B5563] flex-1">{a.name}</span>
                              <span className="text-[10px] text-[#9CA3AF]">{g.name}</span>
                              <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{a.data_type}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                      {(entity.ungrouped_attributes || []).map(a => (
                        <div key={a.attribute_id} className="flex items-center gap-3 px-4 py-1.5 pl-12 border-b border-[#E5E7EB] last:border-b-0">
                          <Checkbox checked={selectedAttrs.has(a.attribute_id)} onCheckedChange={() => toggleAttr(a.attribute_id)} className="h-3.5 w-3.5 rounded-none" data-testid={`check-${a.attribute_id}`} />
                          <span className="text-sm text-[#4B5563] flex-1">{a.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{a.data_type}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Create button */}
          <Button onClick={handleCreate} disabled={creating || !title.trim() || !selectedSchema || selectedAttrs.size === 0} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585] h-10" data-testid="create-proposal-submit">
            <Share2 className="w-4 h-4 mr-2" /> {creating ? 'Creating...' : 'Create Proposal'}
          </Button>
        </div>
      </div>
    </div>
  );
}
